import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import Select, { components, CSSObjectWithLabel, GroupBase, StylesConfig } from 'react-select';
import { ClearIndicatorProps as ReactSelectClearIndicatorProps } from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { ClearIndicatorProps } from 'react-select';
import { setLeadFilter } from '../../actions/actions';

import { FilterParams } from './leadsGrid/leads.types';

import { CalendarDays, Tags, Users, Flag, XCircle, ChevronDown, Search, HelpCircle } from 'lucide-react';
import styled from 'styled-components';
import logger from '../../utils/logger';
import Tooltip from '../../components/Tooltip';

type StylesType = StylesConfig<any, false, GroupBase<any>>;

// ============================================
// 📚 TOOLTIP DEFINITIONS FOR QUICK FILTERS
// ============================================
const quickFilterTooltips = {
  date: "Filter leads by date added (Today, This Week, This Month, etc.)",
  status: "Filter by lead status",
  label: "Filter by  lead labels",
  teamMembers: "View leads by by team members",
  assignedTo: "View only assigned leads"
};

// ============================================
// 📝 DROPDOWN DESCRIPTIONS (shown when opened)
// ============================================
const dropdownDescriptions = {
  date: "Filter by date range",
  status: "Filter by lead status",
  label: "Filter by labels/tags",
  teamMembers: "Filter by team member",
  assignedTo: "Show assigned leads only"
};





interface DropdownOption {
  value: string;
  label: string;
}

interface MenuPosition {
  top: number;
  left: number;
  width: number;
}

interface MenuState {
  isOpen: boolean;
  position: MenuPosition | null;
  activeKey: string | null;
}

interface DropdownProps {
  options: DropdownOption[];
  value: DropdownOption[] | null;
  onChange: (selected: DropdownOption[]) => void;
  placeholder?: string;
  icon: React.ComponentType<{ size: number }>;
  label: string;
}

// State type
interface DropdownState {
  isOpen: boolean;
}



  interface QuickFiltersProps {
    // Change the prop name in the interface
    onFilterChange?: (filterData: Partial<FilterParams>) => void;
  }

interface CustomClearIndicatorProps extends ReactSelectClearIndicatorProps<any> {
  children: ReactNode;
}

// FIND the FilterState interface and UPDATE:
interface FilterState extends FilterParams {
  [key: string]: any;
  sort?: {
    orderBy?: string;
    isAscending?: boolean;
  };
  paginationParams?: {
    page: number;
    perPage: number;
  };
  teamMembers?: string[]; // ADD this line
}




const getDateRange = (dateType: string): { startedAt?: string; endedAt?: string } => {
  if (!dateType) return {};

  const now = new Date();

  // Helper to get UTC date string for start of day (00:00:00.000Z)
  const getUTCStartOfDay = (year: number, month: number, day: number): string => {
    return new Date(Date.UTC(year, month, day, 0, 0, 0, 0)).toISOString();
  };

  // Helper to get UTC date string for end of day (23:59:59.999Z)
  const getUTCEndOfDay = (year: number, month: number, day: number): string => {
    return new Date(Date.UTC(year, month, day, 23, 59, 59, 999)).toISOString();
  };

  const year = now.getFullYear();
  const month = now.getMonth();
  const day = now.getDate();

  switch(dateType) {
    case 'today': {
      return {
        startedAt: getUTCStartOfDay(year, month, day),
        endedAt: getUTCEndOfDay(year, month, day)
      };
    }
    case 'yesterday': {
      const yesterday = new Date(year, month, day - 1);
      return {
        startedAt: getUTCStartOfDay(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate()),
        endedAt: getUTCEndOfDay(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate())
      };
    }
    case 'thisWeek': {
      const dayOfWeek = now.getDay();
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Monday as start
      const weekStart = new Date(year, month, day + diff);
      return {
        startedAt: getUTCStartOfDay(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate()),
        endedAt: getUTCEndOfDay(year, month, day)
      };
    }
    case 'lastWeek': {
      const dayOfWeek = now.getDay();
      const lastWeekEnd = new Date(year, month, day - dayOfWeek - 1); // Last Sunday
      const lastWeekStart = new Date(year, month, day - dayOfWeek - 7); // Last Monday
      return {
        startedAt: getUTCStartOfDay(lastWeekStart.getFullYear(), lastWeekStart.getMonth(), lastWeekStart.getDate()),
        endedAt: getUTCEndOfDay(lastWeekEnd.getFullYear(), lastWeekEnd.getMonth(), lastWeekEnd.getDate())
      };
    }
    case 'last15Days': {
      const start = new Date(year, month, day - 15);
      return {
        startedAt: getUTCStartOfDay(start.getFullYear(), start.getMonth(), start.getDate()),
        endedAt: getUTCEndOfDay(year, month, day)
      };
    }
    case 'lastMonth': {
      const lastMonthStart = new Date(year, month - 1, 1);
      const lastMonthEnd = new Date(year, month, 0); // Last day of last month
      return {
        startedAt: getUTCStartOfDay(lastMonthStart.getFullYear(), lastMonthStart.getMonth(), lastMonthStart.getDate()),
        endedAt: getUTCEndOfDay(lastMonthEnd.getFullYear(), lastMonthEnd.getMonth(), lastMonthEnd.getDate())
      };
    }
    case 'thisMonth': {
      return {
        startedAt: getUTCStartOfDay(year, month, 1),
        endedAt: getUTCEndOfDay(year, month, day)
      };
    }
    case 'last30Days': {
      const start = new Date(year, month, day - 30);
      return {
        startedAt: getUTCStartOfDay(start.getFullYear(), start.getMonth(), start.getDate()),
        endedAt: getUTCEndOfDay(year, month, day)
      };
    }
    case 'thisQuarter': {
      const startMonth = Math.floor(month / 3) * 3;
      return {
        startedAt: getUTCStartOfDay(year, startMonth, 1),
        endedAt: getUTCEndOfDay(year, month, day)
      };
    }
    case 'lastQuarter': {
      const lastQStartMonth = Math.floor(month / 3) * 3 - 3;
      const lastQEndDate = new Date(year, lastQStartMonth + 3, 0);
      return {
        startedAt: getUTCStartOfDay(year, lastQStartMonth, 1),
        endedAt: getUTCEndOfDay(lastQEndDate.getFullYear(), lastQEndDate.getMonth(), lastQEndDate.getDate())
      };
    }
    case 'thisYear': {
      return {
        startedAt: getUTCStartOfDay(year, 0, 1),
        endedAt: getUTCEndOfDay(year, month, day)
      };
    }
    default:
      return {};
  }
};



const dateLeadAdded = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "thisWeek", label: "This Week" },
  { value: "lastWeek", label: "Last Week" },
  { value: "last15Days", label: "Last 15 Days" },
  { value: "thisMonth", label: "This Month" },
  { value: "last30Days", label: "Last 30 Days" },
  { value: "lastMonth", label: "Last Month" },
  { value: "thisQuarter", label: "This Quarter" },
  { value: "lastQuarter", label: "Last Quarter" },
  { value: "thisYear", label: "Year till date" }
];




const QuickFilters: React.FC<QuickFiltersProps> = ({ onFilterChange }) => {
  const dispatch = useDispatch();
  const { filterParam } = useSelector((state: any) => state.rootReducers.leads);
  const userPreferences = useSelector((state: any) => state.rootReducers.user?.userPreferences);
  const [openDropdowns, setOpenDropdowns] = useState<{ [key: string]: boolean }>({});
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
  const menuTriggerRef = useRef<HTMLDivElement>(null);
  const [searchTerms, setSearchTerms] = useState<{ [key: string]: string }>({});

  const StoreData = useSelector((state: any) => state?.rootReducers);

  // 🔒 SECURITY FIX: Use subordinate data with role-based filtering (same as dashboard)
  const userRole = StoreData?.userData?.userDetails?.role?.name;
  const subordinateData = StoreData?.userData?.userDetails?.subordinateEmployees;

  // 📊 COMPREHENSIVE ROLE LOGGING
  React.useEffect(() => {
    logger.log('🎭 QuickFilters Role Check:', {
      userRole: userRole,
      hasSubordinateData: !!subordinateData,
      subordinateDataKeys: subordinateData ? Object.keys(subordinateData) : []
    });
  }, [userRole, subordinateData]);

  // Calculate filtered employees based on role
  const employee = React.useMemo(() => {
    logger.log('🔒 QuickFilters: Applying role-based employee filtering', { userRole });

    // Super admin gets all organization employees
    if (userRole === 'super_admin') {
      const orgEmployees = StoreData?.userData?.userDetails?.organizationEmployee || [];
      const reportTo = subordinateData?.reportTo || [];
      const teamMembers = subordinateData?.teamMembers || [];
      const orgMembers = subordinateData?.organizationMember || [];

      logger.log('👑 Super admin employee data:', {
        orgEmployeesCount: orgEmployees.length,
        reportToCount: reportTo.length,
        teamMembersCount: teamMembers.length,
        orgMembersCount: orgMembers.length
      });

      // ✅ FIX: If orgEmployees is empty but subordinateData exists, use subordinateData
      if (orgEmployees.length === 0 && (reportTo.length > 0 || teamMembers.length > 0 || orgMembers.length > 0)) {
        const allSubordinates = [...reportTo, ...teamMembers, ...orgMembers];
        // Remove duplicates
        const uniqueEmployees = allSubordinates.filter((user, index, array) =>
          array.findIndex(u => u._id === user._id) === index
        );

        logger.log('👑 Super admin using subordinate data:', {
          source: 'subordinateEmployees (fallback)',
          count: uniqueEmployees.length
        });

        return uniqueEmployees;
      }

      logger.log('👑 Super admin using employees:', {
        source: 'organizationEmployee',
        count: orgEmployees.length
      });

      return orgEmployees;
    }

    // Get subordinate arrays
    const reportTo = subordinateData?.reportTo || [];
    const teamMembers = subordinateData?.teamMembers || [];
    const orgMembers = subordinateData?.organizationMember || [];

    logger.log('📊 Subordinate data breakdown:', {
      reportToCount: reportTo.length,
      teamMembersCount: teamMembers.length,
      orgMembersCount: orgMembers.length
    });

    let filteredUsers: any[] = [];

    if (userRole === 'employee') {
      // 🔒 EMPLOYEES: Can ONLY see themselves
      logger.log('🔒 Employee: Restricting to self only');
      const currentUser = {
        firstName: StoreData?.userData?.userDetails?.firstName || '',
        lastName: StoreData?.userData?.userDetails?.lastName || '',
        _id: StoreData?.userData?.userDetails?._id,
        email: StoreData?.userData?.userDetails?.email || '',
      };
      filteredUsers = [currentUser];

    } else if (userRole === 'manager' || userRole === 'team_lead') {
      // 🔓 MANAGERS/TEAM LEADS: Can see their subordinates
      logger.log('🔓 Manager/Team Lead: Showing subordinates');
      const allSubordinates = [...reportTo, ...teamMembers];
      // Remove duplicates
      filteredUsers = allSubordinates.filter((user, index, array) =>
        array.findIndex(u => u._id === user._id) === index
      );

    } else {
      // 🔓 OTHER ROLES: Can see organization members
      logger.log('🔓 Other role: Showing org members');
      const allUsers = [...reportTo, ...teamMembers, ...orgMembers];
      // Remove duplicates
      filteredUsers = allUsers.filter((user, index, array) =>
        array.findIndex(u => u._id === user._id) === index
      );
    }

    logger.log('✅ QuickFilters: Filtered employees', {
      role: userRole,
      count: filteredUsers.length,
      source: userRole === 'super_admin' ? 'organizationEmployee or fallback' : 'subordinateEmployees'
    });

    return filteredUsers;
  }, [userRole, subordinateData, StoreData?.userData?.userDetails]);

 
  React.useEffect(() => {
    // Close dropdown after selection if there are any active filters
    if (filterParam.status?.length || filterParam.label?.length) {
      setOpenDropdowns({});
    }
  }, [filterParam]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is inside any dropdown trigger or menu
      const dropdownElements = document.querySelectorAll('.quickfilter-dropdown, .quickfilter-options-menu');
      let clickedInsideDropdown = false;

      dropdownElements.forEach(element => {
        if (element.contains(target)) {
          clickedInsideDropdown = true;
        }
      });

      if (!clickedInsideDropdown) {
        setOpenDropdowns({});
        setSearchTerms({}); // Clear search terms when closing
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter options based on search term
  const getFilteredOptions = (key: string, options: DropdownOption[]) => {
    const searchTerm = searchTerms[key]?.toLowerCase() || '';
    if (!searchTerm) return options;

    return options.filter(option =>
      option.label.toLowerCase().includes(searchTerm)
    );
  };
  

  
// In QuickFilters.tsx

// Update the handleFilterChange function to properly type-check date ranges
const handleFilterChange = (key: string, selected: any) => {


  if (!selected || (Array.isArray(selected) && selected.length === 0)) {
    logger.log(`Clearing filter: ${key}`);

    // Special handling for assignedTo filter clearing - clear both isAssign and teamMembers
    if (key === 'assignedTo') {
      if (onFilterChange) {
        onFilterChange({
          isAssign: undefined,
          teamMembers: undefined,
          paginationParams: { page: 1, perPage: 50 }
        });
      }
    } else {
      // If clearing a filter, just call onFilterChange with undefined
      if (onFilterChange) {
        onFilterChange({
          [key]: undefined,
          paginationParams: { page: 1, perPage: 50 }
        });
      }
    }

    // Close dropdown after clearing
    setOpenDropdowns(prev => ({ ...prev, [key]: false }));

    return;
  }

  if (key === 'date') {
    logger.log('Processing date filter:', selected.value);

    const dateRange = getDateRange(selected.value);
    logger.log('Converted date range:', dateRange);

    // Only add date property when both startedAt and endedAt are defined
    if (dateRange.startedAt && dateRange.endedAt) {
      const filterUpdate: Partial<FilterParams> = {
        paginationParams: { page: 1, perPage: 50 }
      };

      // Add the date property with proper typing
      filterUpdate.date = {
        startedAt: dateRange.startedAt,
        endedAt: dateRange.endedAt
      };

      if (onFilterChange) {
        onFilterChange(filterUpdate);
      }
    } else {
      logger.log('Incomplete date range, not updating filter');
    }

    // Close date dropdown after selection
    setOpenDropdowns(prev => ({ ...prev, [key]: false }));
  } else if (key === 'assignedTo') {
    // Special handling for assignedTo filter - isAssign and teamMembers at TOP LEVEL
    const values = Array.isArray(selected)
      ? selected.map(item => item.value)
      : [selected.value];

    logger.log(`Setting assignedTo filter to:`, values);

    if (onFilterChange) {
      onFilterChange({
        isAssign: true,
        teamMembers: values,
        paginationParams: { page: 1, perPage: 50 }
      });
    }
  } else {
    // For multi-select filters
    const values = Array.isArray(selected)
      ? selected.map(item => item.value)
      : [selected.value];

    logger.log(`Setting ${key} filter to:`, values);

    if (onFilterChange) {
      onFilterChange({
        [key]: values,
        paginationParams: { page: 1, perPage: 50 }
      });
    }
  }


};
  
  // Add clearFilter helper
  const clearFilter = (key: string) => {
    
    logger.log(`Clearing filter: ${key}`);
    
    if (onFilterChange) {
      onFilterChange({ 
        [key]: undefined,
        paginationParams: { page: 1, perPage: 50 }
      });
    }
    
    
  };
  



  // Map values for each filter type
  const computeFilterValues = {
    // Fix date to match the pattern of other working filters
    date: filterParam.date ? dateLeadAdded.find(option => {
      const range = getDateRange(option.value);
      return range.startedAt === filterParam.date.startedAt;
    }) || null : null,

    // These all work fine - don't touch them
    status: filterParam.status?.map((value: string) => {
      const option = userPreferences?.status?.find((s: any) => s.value === value);
      return option ? { value: option.value, label: option.name } : null;
    }).filter(Boolean) || [],

    label: filterParam.label?.map((value: string) => {
      const option = userPreferences?.labels?.find((l: any) => l.value === value);
      return option ? { value: option.value, label: option.name } : null;
    }).filter(Boolean) || [],

    // Users filter - ONLY show when isAssign is NOT set
    teamMembers: (filterParam.teamMembers && !filterParam.isAssign)
      ? filterParam.teamMembers.map((value: string) => {
          const member = employee?.find((e: any) => e._id === value);
          return member ? {
            value: member._id,
            label: member.firstName && member.lastName
              ? `${member.firstName} ${member.lastName}`
              : member.firstName
          } : null;
        }).filter(Boolean)
      : [],

    // Assigned to filter - ONLY show when isAssign IS set
    assignedTo: (filterParam.isAssign && filterParam.teamMembers)
      ? filterParam.teamMembers.map((value: string) => {
          const member = employee?.find((e: any) => e._id === value);
          return member ? {
            value: member._id,
            label: member.firstName && member.lastName
              ? `${member.firstName} ${member.lastName}`
              : member.firstName
          } : null;
        }).filter(Boolean)
      : []
  };

  
  // QuickFilters array with proper typing
const quickFilters = [

  {
    key: 'date',
    label: 'Date',
    icon: CalendarDays,
    width: 'fit-content',
    isMulti: false,
    options: dateLeadAdded,
    value: computeFilterValues.date,
    clearable: true
  },
  {
    key: 'status',
    label: 'Status',
    icon: Flag,
    width: 'fit-content',
    isMulti: true,
    options: userPreferences?.status?.map((s: any) => ({
      value: s.value,
      label: s.name
    })) || [],
    value: computeFilterValues.status,
    clearable: true,
    getFilterValue: (selected: any[]) => selected?.length ? {
      status: selected.map(item => item.value)
    } : null
  },
  {
    key: 'label',
    label: 'Labels',
    icon: Tags,
    width: 'fit-content',
    isMulti: true,
    options: userPreferences?.labels?.map((l: any) => ({
      value: l.value,
      label: l.name
    })) || [],
    value: computeFilterValues.label,
    clearable: true,
    getFilterValue: (selected: any[]) => selected?.length ? {
      label: selected.map(item => item.value)
    } : null
  },
  {
    key: 'teamMembers',
    label: 'Users',
    icon: Users,
    width: 'fit-content',
    isMulti: true,
    options: employee?.map((v: any) => ({
      value: v._id,
      label: v.firstName && v.lastName ? `${v.firstName} ${v.lastName}` : v.firstName
    })) || [],
    value: computeFilterValues.teamMembers,
    clearable: true,
    getFilterValue: (selected: any[]) => selected?.length ? {
      teamMembers: selected.map(item => item.value)
    } : null
  },
  {
    key: 'assignedTo',
    label: 'Assigned',
    icon: Users,
    width: 'fit-content',
    isMulti: true,
    options: employee?.map((v: any) => ({
      value: v._id,
      label: v.firstName && v.lastName ? `${v.firstName} ${v.lastName}` : v.firstName
    })) || [],
    value: computeFilterValues.assignedTo,
    clearable: true,
    getFilterValue: (selected: any[]) => selected?.length ? {
      isAssign: true,
      teamMembers: selected.map(item => item.value)
    } : null
  }
] as const;

  // Debug: Log filter count
  React.useEffect(() => {
    logger.log('🎯 QuickFilters: Rendering filters', {
      filterCount: quickFilters.length,
      filterKeys: quickFilters.map(f => f.key),
      filterLabels: quickFilters.map(f => f.label),
      assignedToValue: computeFilterValues.assignedTo,
      teamMembersValue: computeFilterValues.teamMembers,
      assignedToIndex: quickFilters.findIndex(f => f.key === 'assignedTo'),
      EXPECTED_COUNT: 5 // Should have exactly 5 filters!
    });

    // Verify assignedTo filter exists
    const hasAssignedTo = quickFilters.some(f => f.key === 'assignedTo');
    if (!hasAssignedTo) {
      logger.error('❌ MISSING: assignedTo filter is not in quickFilters array!');
    } else {
      logger.log('✅ CONFIRMED: assignedTo filter is in the array');
    }
  }, [quickFilters.length, computeFilterValues.assignedTo, computeFilterValues.teamMembers]);

  return (
    // ============================================
    // 🎯 QUICK FILTERS - Should show 5 filters:
    // 1. Date | 2. Status | 3. Labels | 4. Users | 5. Assigned to
    // ============================================
<>
  {quickFilters.map(({ key, label, icon: Icon, options, width, isMulti, value }, index) => {
    // Check if filter has value (for styling)
    const hasValue = key === 'date' ? !!value : (Array.isArray(value) && value.length > 0);

    logger.log(`🔍 Rendering filter ${index + 1}/${quickFilters.length}: ${key}`, {
      label,
      hasValue,
      optionsCount: options?.length,
      width
    });

    return (
      <React.Fragment key={key}>
        <Separator>•</Separator>
        <FilterWrapper width={width}>
          <DropdownContainer className="quickfilter-dropdown">
          <Tooltip
            content={quickFilterTooltips[key as keyof typeof quickFilterTooltips]}
            position="bottom"
            maxWidth={280}
          >
            <DropdownTrigger
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  // Close all other dropdowns, toggle only this one
                  setOpenDropdowns(prev => ({
                    [key]: !prev[key]
                  }));
                }}
                className={hasValue ? 'has-value' : ''}
              >
              <PlaceholderContent>
  <Icon size={14} />
  <span>
    {key === 'date' ? 
      (value ? value.label : label) : // Handle date differently
      (value?.length ? 
        value.length === 1 ? 
          value[0].label : 
          `${value[0].label} & ${value.length - 1} others` 
        : label)
    }
  </span>
</PlaceholderContent>
              <ChevronIcon />
            </DropdownTrigger>
          </Tooltip>

            {openDropdowns[key] && (
  <OptionsMenu $position={menuPosition} className="quickfilter-options-menu">
    {/* Description Header */}
    <DropdownDescription>
      {dropdownDescriptions[key as keyof typeof dropdownDescriptions]}
    </DropdownDescription>

    {/* Search Input */}
    <SearchInputContainer>
      <Search size={14} style={{ color: '#9ca3af', flexShrink: 0 }} />
      <SearchInput
        type="text"
        placeholder={`Search ${label.toLowerCase()}...`}
        value={searchTerms[key] || ''}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          e.stopPropagation();
          setSearchTerms(prev => ({ ...prev, [key]: e.target.value }));
        }}
        onClick={(e: React.MouseEvent<HTMLInputElement>) => e.stopPropagation()}
        autoFocus
      />
    </SearchInputContainer>

    {/* Options List */}
    <OptionsListContainer>
      {getFilteredOptions(key, options).length > 0 ? (
        getFilteredOptions(key, options).map((option: DropdownOption) => (
          <OptionItem
            key={option.value}
            onClick={(e: React.MouseEvent<HTMLDivElement>) => {
              e.stopPropagation();
              if (key === 'date') {
                // Handle date selection - single select
                handleFilterChange(key, option);
              } else {
                // Handle multi-select filters
                const newValue = value?.some((v: DropdownOption) => v.value === option.value)
                  ? value.filter((v: DropdownOption) => v.value !== option.value)
                  : [...(value || []), option];
                handleFilterChange(key, newValue);
              }
            }}
          >
            {key !== 'date' && (
              <Checkbox
                checked={value?.some((v: DropdownOption) => v.value === option.value)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  e.stopPropagation();
                }}
              />
            )}
            <OptionLabel>{option.label}</OptionLabel>
          </OptionItem>
        ))
      ) : (
        <NoResultsMessage>No results found</NoResultsMessage>
      )}
    </OptionsListContainer>
  </OptionsMenu>
)}
          </DropdownContainer>
        </FilterWrapper>
      </React.Fragment>
    );
  })}
</>
  );
};

// Styled components
const FilterWrapper = styled.div<{ width: string }>`
  min-width: 5rem;
  width: ${props => props.width || 'fit-content'};
  max-width: 10rem;
  z-index: 100;
  margin: 0;
  position: relative;
  flex-shrink: 0;

  .select__clear-indicator {
    cursor: pointer;
    padding: 0 4px;
    &:hover {
      color: #ef4444;
    }
  }

  .select__multi-value__remove {
    padding: 0 2px;
    &:hover {
      background: #f3f4f6;
      color: #ef4444;
    }
  }
`;

const PlaceholderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #64748b;
  font-size: 12px;
`;

const Separator = styled.span`
  color: rgb(0, 0, 0);
  margin: 0 3px;
  font-size: 5px;
  line-height: 1;
  user-select: none;
  vertical-align: middle;
  font-weight: bold;
`;

// Select component styles
const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: '1.625rem',
    height: '1.625rem',
    backgroundColor: state.isFocused ? '#dbeafe' : '#f8fafc',
    border: `1px solid ${state.isFocused ? '#93c5fd' : '#e2e8f0'}`,
    borderRadius: '0.25rem',
    boxShadow: 'none',
    '&:hover': {
      borderColor: '#60a5fa',
      backgroundColor: '#dbeafe'
    },
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: '0 0.25rem',
    height: '1.625rem',
    fontSize: '0.8125rem'
  }),
  input: (base: any) => ({
    ...base,
    margin: 0,
    padding: 0
  }),
  placeholder: (base: any) => ({
    ...base,
    color: '#64748b',
    fontSize: '0.8125rem',
    margin: 0
  }),
  indicatorsContainer: (base: any) => ({
    ...base,
    height: '1.625rem'
  }),
  indicatorSeparator: () => ({
    display: 'none'
  }),
  dropdownIndicator: (base: any) => ({
    ...base,
    padding: '0 0.25rem',
    color: '#64748b',
    '&:hover': {
      color: '#475569'
    }
  }),
  clearIndicator: (
    base: Record<string, any>,
    props: ReactSelectClearIndicatorProps<unknown>
  ) => ({
    ...base,
    cursor: 'pointer',
    padding: '0 0.25rem',
    '&:hover': {
      color: '#ef4444'
    }
  }),
  menu: (base: any) => ({
    ...base,
    fontSize: '0.8125rem',
    marginTop: '0.125rem',
    boxShadow: '0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1), 0 0.125rem 0.25rem -0.0625rem rgba(0, 0, 0, 0.06)',
    border: '1px solid #e2e8f0',
    borderRadius: '0.25rem'
  }),
  menuPortal: (base: any) => ({
    ...base,
    zIndex: 9999
  }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: '0.8125rem',
    padding: '0.375rem 0.5rem',
    backgroundColor: state.isSelected ? '#2563eb' : state.isFocused ? '#f1f5f9' : 'white',
    color: state.isSelected ? 'white' : '#1e293b',
    '&:hover': {
      backgroundColor: state.isSelected ? '#2563eb' : '#f1f5f9'
    },
    cursor: 'pointer'
  }),
  multiValue: (base: any) => ({
    ...base,
    backgroundColor: '#dbeafe',
    borderRadius: '0.1875rem',
    margin: '0.125rem'
  }),
  multiValueLabel: (base: any) => ({
    ...base,
    color: '#1e40af',
    fontSize: '0.75rem',
    padding: '0.125rem 0.25rem'
  }),
  multiValueRemove: (base: any) => ({
    ...base,
    color: '#1e40af',
    padding: '0 0.25rem',
    '&:hover': {
      backgroundColor: '#bfdbfe',
      color: '#1e40af'
    },
    cursor: 'pointer'
  })
};

const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
  &.dropdown-container {
    z-index: 50;
  }
`;

const DropdownTrigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 24px;
  padding: 0 6px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 12px;

  &:hover {
    background: #dbeafe;
    border-color: #60a5fa;
  }

  &.has-value {
    background: #dbeafe;
    border-color: #93c5fd;
    color: #1e40af;
  }
`;

// Fix the OptionsMenu styling in our custom dropdown
const OptionsMenu = styled.div<{ $position?: MenuPosition | null }>`
  position: fixed;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15), 0 5px 10px rgba(0, 0, 0, 0.1);
  z-index: 9999;
  width: 220px;
  max-height: 350px;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  ${({ $position }) => $position && `
    top: ${$position.top}px;
    left: ${$position.left}px;
  `}
`;

const OptionItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 4px;
  margin: 1px 6px;

  &:hover {
    background: #f1f5f9;
  }

  &:active {
    background: #e2e8f0;
  }
`;

const OptionLabel = styled.span`
  margin-left: 8px;
  font-size: 13px;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 500;
  max-width: 160px;
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  margin: 0;
  cursor: pointer;
  width: 14px;
  height: 14px;
  accent-color: #0d6efd;
`;

const ChevronIcon = styled(ChevronDown)`
  width: 14px;
  height: 14px;
  color: currentColor;
  margin-left: 4px;
`;

// Dropdown description banner
const DropdownDescription = styled.div`
  padding: 8px 12px;
  background: linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%);
  border-bottom: 1px solid #93c5fd;
  font-size: 11px;
  line-height: 1.4;
  color: #1e40af;
  font-weight: 500;
  text-align: center;
  border-radius: 8px 8px 0 0;
`;

// Search input container
const SearchInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
  position: sticky;
  top: 0;
  z-index: 10;
`;

// Search input field
const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 12px;
  color: #1e293b;
  font-weight: 500;
  min-width: 0;

  &::placeholder {
    color: #94a3b8;
    font-weight: 400;
  }
`;

// Options list container
const OptionsListContainer = styled.div`
  max-height: 250px;
  overflow-y: auto;
  padding: 4px 0;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
    margin: 4px 0;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 0.25rem;
    transition: background 0.2s;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

// No results message
const NoResultsMessage = styled.div`
  padding: 2rem 1.25rem;
  text-align: center;
  color: #94a3b8;
  font-size: 0.875rem;
  font-style: italic;
`;

export default QuickFilters;